import { Icon } from '@ps/isp-web-component-library';
import { Cash } from '@ps/isp-web-component-library/icons/Cash';
import { FreeBetSb } from '@ps/isp-web-component-library/icons/FreeBetSb';
import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { WalletTypeEnum } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { TrackingContext, inputFilled, useTrackers } from '@ps/web-shared-analytics';
import { useTrackingLabel } from '@ps/web-shared-components';
import React, { useCallback, useContext, useEffect } from 'react';
import { useField, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import {
  getIsAvailableOpportunity,
  getIsMultipleAvailableOpportunity,
  getIsSingleAvailableOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import useIsMultipleStartingPriceOpportunity from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useIsMultipleStartingPriceOpportunity';

import { useHasPromotionApplied } from '../../../../../../hooks/useHasPromotionApplied';
import usePromotionCheckbox from '../../../../../../hooks/usePromotionCheckbox';
import usePromotionPicker from '../../../../../../hooks/usePromotionPicker';
import { getIsComplexMultiple } from '../../../../../../utils/getIsComplexMultiple';
import getIsDefaultStartingPriceSelected from '../../../../../../utils/getIsDefaultStartingPriceSelected';
import { getPromotionPickerIcon } from '../../../../../../utils/getPromotionPickerIcon';

import {
  TXTWCL_Apply_Bonus_Cash,
  TXTWCL_Apply_Free_Bet,
  TXTWCL_Stake
} from './PromotionControl.messages.json';
import styles from './PromotionControl.scss';
import PromotionPickerButton from './components/PromotionPickerButton/PromotionPickerButton';

interface Props {
  fieldName: string;
  numberOfLines: number;
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const PromotionControl: React.FC<Props> = ({ fieldName, numberOfLines, opportunity }) => {
  const { formatMessage } = useRegulatoryIntl();
  const { submitting: isSubmitting } = useFormState({ subscription: { submitting: true } });
  const isPromotionPickerEnabled = useFeature(FeatureFlag.ISPPromotionPicker);
  const dispatch = useDispatch<Dispatch>();
  const { buttonPressed, promotionRemoved } = useTrackers();
  const tracking = useContext(TrackingContext);
  const trackingLabelFieldName = useTrackingLabel(TXTWCL_Stake);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);

  const isPromotionPickerOpen = useSelector((rootState: RootState) =>
    select.betSlip.isPromotionPickerOpen(rootState, opportunity.id)
  );
  const selectedPromotionPicker = useSelector(select.betSlip.selectedPromotionPicker);
  const extraWallets = useSelector(select.extraWallets.getAllEnabledWallets);
  const selectedPromoId = useSelector(select.betSlip.selectedPromotion);
  const selectedPromoWallet = extraWallets.find((wallet) => wallet.id === selectedPromoId);

  const {
    promotionFieldName,
    promotionId,
    promotionInput,
    promotionRedeemable,
    isEligibleForPromotion,
    isFeatureEnabled
  } = usePromotionCheckbox(WalletTypeEnum.BONUS_CASH, opportunity, fieldName, numberOfLines);
  const { clearPromotionFieldValue } = usePromotionPicker(
    opportunity,
    opportunity.id,
    numberOfLines,
    selectedPromoWallet?.id
  );
  const { input: eachWayInput } = useField(`${fieldName}.eachWaySelected`, {
    type: 'checkbox',
    subscription: { value: true }
  });
  const { hasFreeBetApplied, hasBonusCashApplied, hasPromotionApplied, hasOtherPromotionApplied } =
    useHasPromotionApplied(fieldName);
  const isOtherPromotionPickerOpen = !isPromotionPickerOpen && Boolean(selectedPromotionPicker);

  const isComplexMultiple = getIsComplexMultiple(numberOfLines, eachWayInput.checked);

  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isMultipleAvailableOpportunity = getIsMultipleAvailableOpportunity(opportunity);
  const isDefaultStartingPriceSelected = getIsDefaultStartingPriceSelected(opportunity);
  const isMultipleStartingPriceOpportunity = useIsMultipleStartingPriceOpportunity(opportunity);
  const { input: potentialReturns } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const { startingPriceSelected = isDefaultStartingPriceSelected } = potentialReturns.value || {};

  const onPromotionRemove = useCallback(() => {
    clearPromotionFieldValue();
    promotionRemoved(
      selectedPromoWallet
        ? {
            ...selectedPromoWallet,
            expiry: new Date(selectedPromoWallet.expiry).getTime()
          }
        : undefined
    );
    dispatch.betSlip.promotionRemoved();
    inputFilled(
      {
        category: tracking.category,
        widget: tracking.widget,
        isMobileEmbedded
      },
      {
        fieldName: trackingLabelFieldName,
        numInput: 0,
        fieldType: 'number'
      }
    );
  }, [
    clearPromotionFieldValue,
    dispatch.betSlip,
    isMobileEmbedded,
    promotionRemoved,
    selectedPromoWallet,
    tracking.category,
    tracking.widget,
    trackingLabelFieldName
  ]);

  let isPriceOddsSelected = true;
  if (isSingleAvailableOpportunity) {
    isPriceOddsSelected = Boolean(!startingPriceSelected && opportunity.winRunnerOdds);
  } else if (isMultipleAvailableOpportunity) {
    isPriceOddsSelected = !isMultipleStartingPriceOpportunity;
  }

  useEffect(() => {
    if (isPriceOddsSelected === false && hasPromotionApplied) {
      onPromotionRemove();
    }
  }, [isPriceOddsSelected, hasPromotionApplied, onPromotionRemove]);

  if (
    !isEligibleForPromotion ||
    !isFeatureEnabled ||
    isComplexMultiple ||
    isOtherPromotionPickerOpen ||
    hasOtherPromotionApplied ||
    !getIsAvailableOpportunity(opportunity) ||
    !isPriceOddsSelected
  ) {
    return null;
  }

  const eligiblePromotions = opportunity.applicableWallets?.length || 0;

  if (isPromotionPickerEnabled && eligiblePromotions > 1) {
    if (extraWallets.length < 2) {
      // race-condition: while waiting for extraWallets call to complete
      return null;
    }

    const { icon, size } = getPromotionPickerIcon(
      extraWallets,
      opportunity,
      isPromotionPickerOpen,
      hasFreeBetApplied,
      hasBonusCashApplied
    );

    if (isPromotionPickerOpen) {
      return (
        <PromotionPickerButton
          onClick={() => {
            buttonPressed({ buttonName: 'Close Promotion Picker' });
            dispatch.betSlip.promotionPickerToggled(undefined);
          }}
          icon={icon}
          size={size}
          variant="close"
        />
      );
    }

    if (hasPromotionApplied) {
      return (
        <PromotionPickerButton
          onClick={onPromotionRemove}
          icon={icon}
          size={size}
          variant="remove"
        />
      );
    }

    return (
      <PromotionPickerButton
        onClick={() => {
          buttonPressed({ buttonName: 'Open Promotion Picker' });
          dispatch.betSlip.promotionPickerToggled(opportunity.id);
        }}
        icon={icon}
        size={size}
        variant="open"
      />
    );
  }

  const isBonusCash = promotionRedeemable === true;

  return (
    <label className={styles.extraWalletLabel} data-testid="opportunity-promotion-control">
      <input
        {...promotionInput}
        type="checkbox"
        value={promotionId}
        disabled={Boolean(promotionFieldName && promotionFieldName !== fieldName) || isSubmitting}
        className={styles.srOnly}
      />
      <span className={styles.promotionCheckbox}>
        {isBonusCash ? (
          <Icon icon={Cash} size="x1" label={formatMessage(TXTWCL_Apply_Bonus_Cash)} />
        ) : (
          <Icon
            icon={FreeBetSb}
            size="sm"
            className={styles.freeBetIcon}
            label={formatMessage(TXTWCL_Apply_Free_Bet)}
          />
        )}
      </span>
    </label>
  );
};

export default PromotionControl;
